import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "../../layouts";
import { toast } from "react-toastify";
import DynamicTable from "../../components/DynamicTable";
import apiClient from "../../utils/apiClient";
import apiService from "../../services/apiService";
import { withSearchIcon } from "../../utils/helper";

export default function ScannedTickets() {
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventIdInput, setEventIdInput] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const pageSize = 50;

  // ✅ Get eventId from query params (if available)

  const navigate = useNavigate();
  const location = useLocation();
  const queryEventId = new URLSearchParams(location.search).get("eventId");

  const [eventId, setEventId] = useState<any>(queryEventId);

  const columns = [
    { key: "ticket_name", label: withSearchIcon("Ticket Name") },
    {
      key: "ticket_price",
      label: "Price",
      render: (val: string | number) => `${val}`,
      isDecimal: true,
      isAmount: true,
    },
    { key: "ticket_limit", label: "Limit" },
    {
      key: "is_passworded",
      label: "Password Protected",
      render: (val: boolean) =>
        val === true ? (
          <span className="text-green-700 font-semibold">Yes</span>
        ) : (
          <span className="text-gray-500">No</span>
        ),
    },
    {
      key: "password",
      label: "Password",
      render: (_:any, val: any) =>
        val.is_passworded  && val?.password && val?.password?.length > 0  ? (
          <button
            onClick={() => {
              navigator.clipboard.writeText(val?.password);
              toast.success("Password copied!");
            }}
            className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 transition"
          >
            Copy
          </button>
        ) : (
          "—"
        ),
    },
    {
      key: "sale_start_date",
      label: "Sale Start(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "sale_end_date",
      label: "Sale End(Local)",
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "ticket_description",
      label: "Description",
      render: (val: string) => val || "—",
    },
    { key: "event_id", label: "Event Id" },
    { key: "event_name", label: withSearchIcon("Event Name") },
    {
      key: "ticket_type_created_at",
      label: "Created At(Local)",
      isDate: true,
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
    {
      key: "ticket_type_updated_at",
      label: "Updated At(Local)",
      isDate: true,
      render: (val: string) =>
        val
          ? new Date(val).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "—",
    },
  ];

  useEffect(() => {
    fetchTickets(eventId, page, search);
  }, [eventId, page, search]);

  const fetchTickets = async (
    eventId?: number,
    activePageNo?: number,
    searchTerm?: any
  ) => {
    try {
      setLoading(true);

      // Build params conditionally
      const params: any = {
        page: activePageNo,
        limit: 50,
        search: searchTerm,
        ...(eventId ? { eventId } : {}), // ✅ adds eventId only if it exists
      };

      const response: any = await apiService.get(
        "/event/ticket/get-event-scanned-tickets",
        params
      );
      setTickets(response?.data?.data || []); // ✅ updated: response.data is now event array
      setTotalRecords(response?.data?.totalCount || 0);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (searchKey?: any) => {
    console.log("Searching for Event ID:", searchKey);
    const params = new URLSearchParams(location.search);

    if (searchKey && searchKey.trim() !== "") {
      // ✅ Add/replace eventId
      params.set("eventId", searchKey.trim());
    } else {
      // ❌ searchKey empty → remove eventId
      params.delete("eventId");
    }

    // 🚀 Update URL (no reload)
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    setEventId(searchKey.trim());
    setPage(1);
  };

  return (
    <Layout
      pageTitle="Scanned Tickets"
      eventId={queryEventId || eventId}
      searchByEventId={{
        label: "Search by Event ID",
        onChange: (val: any) => setEventIdInput(val),
        onClick: (val?: any) => {
          handleSearch(val);
        }, // 👈 your custom handler
        className: "bg-green-600 hover:bg-green-700",
      }}
    >
      <div className="min-h-screen bg-gray-50 py-3 px-4 min-w-0">
        <DynamicTable
          title="Scanned Tickets"
          data={tickets}
          columns={columns}
          pageSize={50}
          loading={loading}
          activeNUmber={page}
          onPageChange={(newPage) => setPage(newPage)}
          totalPages={totalPages}
          totalRecords={totalRecords}
          onSearch={(val?: any) => {
            setPage(1);
            setSearch(val);
          }}
        />
      </div>
    </Layout>
  );
}
